import { isRecord } from './isRecord';
import {
  type AiBuilderChatQuestionSnapshotChangeType,
  type AiBuilderChatQuestionSnapshotQuizType,
} from './types';

export type ServerAiBuilderChatEventDto =
  | ServerAiBuilderChatTurnStartedEventDto
  | ServerAiBuilderChatProgressEventDto
  | ServerAiBuilderChatThinkingDeltaEventDto
  | ServerAiBuilderChatTextDeltaEventDto
  | ServerAiBuilderChatQuestionEventDto
  | ServerAiBuilderChatChipsEventDto
  | ServerAiBuilderChatMultiQuestionEventDto
  | ServerAiBuilderChatConfirmRequiredEventDto
  | ServerAiBuilderChatQuestionsUpdatedEventDto
  | ServerAiBuilderChatNoticeEventDto
  | ServerAiBuilderChatRespondEventDto
  | ServerAiBuilderChatErrorEventDto;

export type ServerAiBuilderChatEventType = ServerAiBuilderChatEventDto['type'];

export type ServerAiBuilderChatTurnMetadataDto = {
  session_id?: string;
  turn?: number;
  turn_id?: string;
  message_id?: string;
};

export type ServerAiBuilderChatUsageInfoDto = {
  state: ServerAiBuilderChatUsageState;
  remaining: number;
  limit: number;
};

export type ServerAiBuilderChatUsageState = 'normal' | 'low' | 'blocked';

export type ServerAiBuilderChatLimitsDto = {
  max_questions: number;
  max_message_length: number;
  quiz_types: ServerAiBuilderChatQuizType[];
};

export type ServerAiBuilderChatSessionResponseDto = {
  session_id: string;
  created_at: string;
  usage: ServerAiBuilderChatUsageInfoDto;
  limits: ServerAiBuilderChatLimitsDto;
};

export type ServerAiBuilderChatSourceResponseDto = {
  source_ref: string;
  source_type: ServerAiBuilderChatSourceType;
  title: string;
  status: ServerAiBuilderChatSourceStatus;
  char_count?: number;
};

export type ServerAiBuilderChatSourceType = 'file' | 'youtube' | 'website';

export type ServerAiBuilderChatSourceStatus = 'ready' | 'failed';

export type ServerAiBuilderChatResetResponseDto = {
  session_id: string;
  chat_cleared: boolean;
  questions_retained: number;
  sources_retained: number;
  undo_invalidated: boolean;
};

export type ServerAiBuilderChatFeedbackResponseDto = {
  feedback: 'good' | 'bad' | null;
};

export type ServerAiBuilderChatHttpErrorDto = {
  code: ServerAiBuilderChatErrorCode;
  message: string;
  /** 같은 code 안에서 상황을 세분하는 서버 i18n 서브코드 (RFC #412). */
  reason?: string;
  usage?: ServerAiBuilderChatUsageInfoDto;
};

export type ServerAiBuilderChatTurnStartedEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'turn_started';
    session_id: string;
  };

export type ServerAiBuilderChatProgressEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'progress';
    stage: ServerAiBuilderChatProgressStage;
    step_id?: string;
    message: string;
    /**
     * 진행 문구의 i18n 코드 (RFC #412). 있으면 FE 카피로 렌더하고,
     * 없거나 미등록 코드면 서버 message 를 그대로 렌더한다.
     */
    message_code?: string | null;
  };

export type ServerAiBuilderChatProgressStage =
  | 'generating'
  | 'searching'
  | 'extracting'
  | 'importing'
  | 'reading'
  | 'applying';

export type ServerAiBuilderChatTextDeltaEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'text_delta';
    delta: string;
  };

/**
 * 모델의 사고 과정 토큰. 답변 본문(text_delta) 이전에 진행 중 카드 안에서
 * 흘려보내는 reasoning 스트림이며, 답변이 시작되거나 턴이 끝나면 사라진다.
 */
export type ServerAiBuilderChatThinkingDeltaEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'thinking_delta';
    delta: string;
    /** 멀티스텝 추론에서의 hop 인덱스 — 분석/디버그용(렌더에는 미사용). */
    hop?: number;
  };

/**
 * question 이벤트의 구조화 선택지 (2026-07-16 리치 카드 계약).
 * - label: main text — 선택지 표시 라벨 (성취기준은 본문)
 * - description: sub text — 보조 설명 (성취기준 해설·학교급/학년/과목).
 *   없으면 기존 1줄 칩으로 렌더한다.
 * - value: canonical id (성취기준 코드, 문항유형 id 등) — 회신·pin 매칭 key
 */
export type ServerAiBuilderChatQuestionOptionDto = {
  label: string;
  description?: string | null;
  value?: string | null;
};

export type ServerAiBuilderChatQuestionEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'question';
    question: string;
    context?: string | null;
    /**
     * 닫힌 선택지 목록. 서버 chips tool 병합(2026-07-03) 이후 선택지 질문도
     * question 이벤트 하나로 오며, options 가 비어 있지 않으면 선택 UI 로 렌더한다.
     * 구조화 계약(2026-07-16) 이후 객체 배열이 정본이다 — string 은 서버 선배포
     * 전 혼재 구간을 위한 전환기 가드로만 수용한다.
     */
    options?: Array<ServerAiBuilderChatQuestionOptionDto | string> | null;
    selection_mode?: ServerAiBuilderChatChipsSelectionMode | null;
    /** 이 질문이 채우려는 슬롯 (예: topic, quiz_types, count) — 분석/추적용. */
    missing_slot?: string | null;
    copy_code?: string | null;
    copy_params?: ServerAiBuilderChatCopyParamsDto | null;
    trace_id?: string;
  };

export type ServerAiBuilderChatChipsEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'chips';
    prompt: string;
    options: string[];
    selection_mode?: ServerAiBuilderChatChipsSelectionMode | null;
  };

export type ServerAiBuilderChatChipsSelectionMode = 'single' | 'multiple';

export type ServerAiBuilderChatMultiQuestionOptionDto = {
  option_id: string;
  label: string;
  description: string | null;
  value: string | null;
};

export type ServerAiBuilderChatMultiQuestionQuestionDto = {
  question_id: string;
  question: string;
  context: string;
  options: ServerAiBuilderChatMultiQuestionOptionDto[];
  selection_mode: ServerAiBuilderChatChipsSelectionMode;
  allow_direct_input: boolean;
  missing_slot: string;
};

export type ServerAiBuilderChatMultiQuestionEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'multi_question';
    interaction_id: string;
    questions: ServerAiBuilderChatMultiQuestionQuestionDto[];
  };

const MULTI_QUESTION_EVENT_KEYS = [
  'type',
  'interaction_id',
  'questions',
  'session_id',
  'turn',
  'turn_id',
  'message_id',
] as const;

const MULTI_QUESTION_QUESTION_KEYS = [
  'question_id',
  'question',
  'context',
  'options',
  'selection_mode',
  'allow_direct_input',
  'missing_slot',
] as const;

const MULTI_QUESTION_OPTION_KEYS = [
  'option_id',
  'label',
  'description',
  'value',
] as const;

/**
 * SSE 경계에서 multi_question payload를 검증한다.
 *
 * 일반 SSE parser는 레거시 이벤트 호환을 위해 JSON을 느슨하게 수용하므로,
 * interaction 상태로 옮기기 전에는 안정 ID와 ordered question 계약을 여기서
 * 별도로 fail-closed한다.
 */
export const isServerAiBuilderChatMultiQuestionEventDto = (
  value: unknown,
): value is ServerAiBuilderChatMultiQuestionEventDto => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, MULTI_QUESTION_EVENT_KEYS) ||
    value.type !== 'multi_question' ||
    !isNonBlankString(value.interaction_id) ||
    !Array.isArray(value.questions) ||
    value.questions.length === 0 ||
    !hasValidTurnMetadata(value)
  ) {
    return false;
  }

  const questionIds = new Set<string>();

  for (const question of value.questions) {
    if (!isServerAiBuilderChatMultiQuestionQuestionDto(question)) return false;
    if (questionIds.has(question.question_id)) return false;

    questionIds.add(question.question_id);
  }

  return true;
};

const isServerAiBuilderChatMultiQuestionQuestionDto = (
  value: unknown,
): value is ServerAiBuilderChatMultiQuestionQuestionDto => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, MULTI_QUESTION_QUESTION_KEYS) ||
    !isNonBlankString(value.question_id) ||
    !isNonBlankString(value.question) ||
    typeof value.context !== 'string' ||
    !Array.isArray(value.options) ||
    (value.selection_mode !== 'single' &&
      value.selection_mode !== 'multiple') ||
    typeof value.allow_direct_input !== 'boolean' ||
    typeof value.missing_slot !== 'string'
  ) {
    return false;
  }

  if (value.options.length === 0 && !value.allow_direct_input) return false;

  const optionIds = new Set<string>();

  for (const option of value.options) {
    if (!isServerAiBuilderChatMultiQuestionOptionDto(option)) return false;
    if (optionIds.has(option.option_id)) return false;

    optionIds.add(option.option_id);
  }

  return true;
};

const isServerAiBuilderChatMultiQuestionOptionDto = (
  value: unknown,
): value is ServerAiBuilderChatMultiQuestionOptionDto =>
  isRecord(value) &&
  hasOnlyKeys(value, MULTI_QUESTION_OPTION_KEYS) &&
  isNonBlankString(value.option_id) &&
  isNonBlankString(value.label) &&
  (value.description === null || typeof value.description === 'string') &&
  (value.value === null || typeof value.value === 'string');

const isNonBlankString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const hasValidTurnMetadata = (
  value: Record<string, unknown>,
): value is Record<string, unknown> & ServerAiBuilderChatTurnMetadataDto =>
  (!('session_id' in value) || typeof value.session_id === 'string') &&
  (!('turn' in value) ||
    (typeof value.turn === 'number' &&
      Number.isFinite(value.turn) &&
      Number.isInteger(value.turn))) &&
  (!('turn_id' in value) || typeof value.turn_id === 'string') &&
  (!('message_id' in value) || typeof value.message_id === 'string');

const hasOnlyKeys = (
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean => {
  const allowedKeySet = new Set(allowedKeys);

  return Object.keys(value).every(key => allowedKeySet.has(key));
};

export type ServerAiBuilderChatConfirmRequiredEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'confirm_required';
    message: string;
    confirm_token: string;
    action_description: string;
    /**
     * 확인 대상 액션의 결정적 식별 코드 (RFC #412). 카피 작성 주체(LLM/서버
     * 폴백)와 무관하게 내려오며, FE 는 code+params 로 자체 카피를 렌더할 수
     * 있다. 없으면 서버 message/label verbatim.
     */
    code?: ServerAiBuilderChatConfirmCode | null;
    params?: ServerAiBuilderChatConfirmParamsDto | null;
    question_refs?: ServerAiBuilderChatQuestionRefDto[];
    options: ServerAiBuilderChatConfirmOptionDto[];
    trace_id?: string;
  };

export type ServerAiBuilderChatConfirmCode =
  | 'CONFIRM_DELETE_QUESTIONS'
  | 'CONFIRM_REDUCE_COUNT'
  | 'CONFIRM_REPLACE_QUESTIONS'
  | 'CONFIRM_ACTION';

export type ServerAiBuilderChatConfirmParamsDto = {
  question_numbers?: number[];
  count?: number;
  target_count?: number;
};

export type ServerAiBuilderChatConfirmOptionDto = {
  label: string;
  decision: ServerAiBuilderChatConfirmDecision;
  style: ServerAiBuilderChatConfirmOptionStyle;
};

export type ServerAiBuilderChatConfirmDecision = 'approve' | 'cancel';

export type ServerAiBuilderChatConfirmOptionStyle = 'destructive' | 'secondary';

export type ServerAiBuilderChatQuestionsUpdatedEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'questions_updated';
    questions: ServerAiBuilderChatQuestionSnapshotDto[];
    total: number;
    changed_question_ids: string[];
    moved_question_ids?: string[];
    change_type: ServerAiBuilderChatQuestionsChangeType;
    generation_id?: string;
    base_question_list_revision?: string;
  };

export type ServerAiBuilderChatQuestionSnapshotDto = {
  question_id: string;
  number: number;
  quiz_type: ServerAiBuilderChatQuizType;
  data: ServerAiBuilderChatQuestionDataDto;
};

export type ServerAiBuilderChatQuestionDataDto = Record<string, unknown>;

export type ServerAiBuilderChatQuizType = AiBuilderChatQuestionSnapshotQuizType;

export type ServerAiBuilderChatQuestionsChangeType =
  AiBuilderChatQuestionSnapshotChangeType;

export type ServerAiBuilderChatNoticeEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'notice';
    code: ServerAiBuilderChatNoticeCode;
    message: string;
    copy_code?: string | null;
    copy_params?: ServerAiBuilderChatCopyParamsDto | null;
  };

export type ServerAiBuilderChatRespondEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'respond';
    text: string;
    trace_id?: string;
    response_type?: ServerAiBuilderChatRespondResponseType;
    question_refs?: ServerAiBuilderChatQuestionRefDto[];
    actions?: ServerAiBuilderChatAssistantActionDto[];
    result_summary?: ServerAiBuilderChatResultSummaryDto;
    halted: boolean;
    undo_available: boolean;
    usage: ServerAiBuilderChatUsageInfoDto;
    copy_code?: string | null;
    copy_params?: ServerAiBuilderChatCopyParamsDto | null;
  };

export type ServerAiBuilderChatCopyParamsDto = Record<string, unknown>;

export type ServerAiBuilderChatRespondResponseType = 'generation_complete';

export type ServerAiBuilderChatResultSummaryDto = {
  type: 'generation';
  requested_count: number;
  created_count: number;
  shortfall_count: number;
  shortfall_reason:
    | 'none'
    | 'under_generated'
    | 'invalid_filtered'
    | 'capacity_limited'
    | 'unknown';
  dropped_count?: number;
};

export type ServerAiBuilderChatAssistantActionDto = {
  id?: string;
  action_id?: string;
  type?: 'prompt';
  label: string;
  prompt: string;
  emoji?: string;
  params?: Record<string, unknown>;
};

export type ServerAiBuilderChatQuestionRefDto = {
  number: number;
  question_id: string;
};

export type ServerAiBuilderChatErrorEventDto =
  ServerAiBuilderChatTurnMetadataDto & {
    type: 'error';
    code: ServerAiBuilderChatErrorCode;
    message: string;
    /** 같은 code 안에서 상황을 세분하는 서버 i18n 서브코드 (RFC #412). */
    reason?: string;
    usage?: ServerAiBuilderChatUsageInfoDto;
  };

export type ServerAiBuilderChatNoticeCode =
  | 'QUESTION_LIMIT_EXCEEDED'
  | 'UNSUPPORTED_URL'
  | 'NON_QUIZ_REQUEST'
  | 'HIGH_RISK_TOPIC'
  | 'SOURCE_REF_UNAVAILABLE';

export type ServerAiBuilderChatErrorCode =
  | ServerAiBuilderChatNoticeCode
  | 'AUTH_EXPIRED'
  | 'USAGE_BLOCKED'
  | 'USAGE_UNAVAILABLE'
  | 'SESSION_LOCKED'
  | 'MESSAGE_TOO_LONG'
  | 'INVALID_REQUEST'
  | 'SESSION_NOT_FOUND'
  | 'SESSION_BINDING_MISMATCH'
  | 'UNDO_UNAVAILABLE'
  | 'CONFIRM_TOKEN_INVALID'
  | 'INTERNAL_ERROR'
  | 'UNSUPPORTED_FORMAT'
  | 'EXTRACTION_FAILED'
  | 'FILE_TOO_LARGE'
  | 'LOCKED_FILE'
  | 'TOO_MANY_PAGES'
  | 'STORAGE_UPLOAD_FAILED'
  | 'GENERATION_EMPTY_RESULT';
